import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Receipt, Camera } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const STATUS_BADGE = {
    envoye: { l: "En attente", c: "border-[#B89C6A] text-[#B89C6A]" },
    signe: { l: "Signé ✓", c: "border-green-600 text-green-700" },
    refuse: { l: "Refusé", c: "border-red-600 text-red-700" },
};

export const DocumentsSection = () => {
    const [proposals, setProposals] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [tab, setTab] = useState("devis");

    useEffect(() => {
        Promise.all([
            axios.get(`${API}/me/proposals`).then((r) => setProposals(r.data.proposals || [])).catch(() => {}),
            axios.get(`${API}/me/invoices`).then((r) => setInvoices(r.data.invoices || [])).catch(() => {}),
            axios.get(`${API}/me/projects`).then((r) => {
                const all = [];
                (r.data.projects || []).forEach((p) => {
                    (p.photos || []).forEach((ph) => all.push({ ...ph, project_title: p.title }));
                });
                all.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
                setPhotos(all);
            }).catch(() => {}),
        ]);
    }, []);

    const downloadBlob = async (url, filename) => {
        try {
            const r = await axios.get(url, { responseType: "blob" });
            const u = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = u;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(u);
        } catch {
            toast.error("Erreur de téléchargement");
        }
    };

    const downloadPhoto = (ph, i) => {
        const a = document.createElement("a");
        a.href = `data:${ph.mime_type};base64,${ph.image_base64}`;
        a.download = `chantier-photo-${i + 1}.${(ph.mime_type || "image/jpeg").split("/")[1]}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    if (proposals.length === 0 && invoices.length === 0 && photos.length === 0) return null;

    const TABS = [
        { k: "devis", l: "Devis", n: proposals.length, icon: FileText },
        { k: "factures", l: "Factures", n: invoices.length, icon: Receipt },
        { k: "photos", l: "Photos de chantier", n: photos.length, icon: Camera },
    ];

    return (
        <section data-testid="my-documents" className="mb-12">
            <h2 className="font-serif-lux text-2xl mb-4 flex items-center gap-3">
                <span className="h-px w-8 bg-[#B89C6A]" />
                Mes documents
            </h2>
            <div className="lux-card p-6">
                <div className="flex flex-wrap gap-2 mb-5">
                    {TABS.map((t) => (
                        <button
                            key={t.k}
                            data-testid={`documents-tab-${t.k}`}
                            onClick={() => setTab(t.k)}
                            className={`inline-flex items-center gap-2 px-4 py-2 border text-[11px] tracking-wider uppercase transition ${tab === t.k ? "border-[#B89C6A] bg-[#B89C6A]/10" : "hairline"}`}
                        >
                            <t.icon size={13} className="text-[#B89C6A]" />
                            {t.l} ({t.n})
                        </button>
                    ))}
                </div>

                {tab === "devis" && (
                    <div className="space-y-2" data-testid="documents-devis-list">
                        {proposals.length === 0 && <p className="text-[13px] text-[#1C1E1D]/50 italic">Aucun devis.</p>}
                        {proposals.map((p) => {
                            const badge = STATUS_BADGE[p.status] || STATUS_BADGE.envoye;
                            return (
                                <div key={p.id} className="flex items-center justify-between gap-3 border hairline px-4 py-3 flex-wrap">
                                    <div className="min-w-0">
                                        <div className="text-[13px]">{p.title}</div>
                                        <div className="text-[11px] text-[#1C1E1D]/50 mt-0.5">
                                            {p.number} · {p.created_at?.slice(0, 10)} · {fmt(p.total_ttc)} € TTC
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0">
                                        <span className={`text-[10px] tracking-wider uppercase px-2.5 py-1 border ${badge.c}`}>{badge.l}</span>
                                        <button
                                            data-testid={`doc-devis-pdf-${p.id}`}
                                            onClick={() => downloadBlob(`${API}/me/proposals/${p.id}/pdf`, `devis-${p.number}.pdf`)}
                                            className="text-[11px] tracking-wider uppercase underline hover:text-[#B89C6A]"
                                        >
                                            PDF ↓
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {tab === "factures" && (
                    <div className="space-y-2" data-testid="documents-factures-list">
                        {invoices.length === 0 && <p className="text-[13px] text-[#1C1E1D]/50 italic">Aucune facture pour l'instant — elles apparaîtront ici à chaque paiement.</p>}
                        {invoices.map((inv) => (
                            <div key={inv.id} className="flex items-center justify-between gap-3 border hairline px-4 py-3 flex-wrap">
                                <div className="min-w-0">
                                    <div className="text-[13px]">{inv.milestone_label} — {inv.title}</div>
                                    <div className="text-[11px] text-[#1C1E1D]/50 mt-0.5">
                                        {inv.number} · {inv.created_at?.slice(0, 10)} · devis {inv.proposal_number}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    <span className="font-serif-lux text-lg text-[#B89C6A]">{fmt(inv.amount_ttc)} €</span>
                                    <button
                                        data-testid={`doc-facture-pdf-${inv.id}`}
                                        onClick={() => downloadBlob(`${API}/invoices/${inv.id}/pdf`, `facture-${inv.number}.pdf`)}
                                        className="text-[11px] tracking-wider uppercase underline hover:text-[#B89C6A]"
                                    >
                                        PDF ↓
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {tab === "photos" && (
                    <div data-testid="documents-photos-grid">
                        {photos.length === 0 && <p className="text-[13px] text-[#1C1E1D]/50 italic">Aucune photo — elles apparaîtront ici au fil de votre chantier.</p>}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                            {photos.map((ph, i) => (
                                <figure key={ph.id || i} className="group relative">
                                    <img
                                        src={`data:${ph.mime_type};base64,${ph.image_base64}`}
                                        alt={ph.caption || ph.project_title || "Photo de chantier"}
                                        className="aspect-square w-full object-cover border hairline"
                                    />
                                    <figcaption className="text-[10px] text-[#1C1E1D]/50 mt-1 truncate">
                                        {ph.project_title} · {ph.created_at?.slice(0, 10)}
                                    </figcaption>
                                    <button
                                        data-testid={`doc-photo-download-${i}`}
                                        onClick={() => downloadPhoto(ph, i)}
                                        className="absolute top-2 right-2 bg-[#1C1E1D]/80 text-[#FAF9F6] text-[10px] px-2 py-1 opacity-0 group-hover:opacity-100 transition"
                                    >
                                        ↓
                                    </button>
                                </figure>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};
